import * as React from "react";
import {isFunction} from "lodash";
import FieldLabel from "../../FieldLabel";
import FieldError from "../../FieldError";
import * as classnames from "classnames";
import Tooltip from "../../../overlays/Tooltip";
import Icon from "../../../icon/Icon";
import {IFormFieldProps} from "../../create_form";
import {FormInput} from "../input/FormInput";


interface IProps extends IFormFieldProps<any> {
    label?: string;
    allowedExtensions?: string[];
    maxSize?: string;
    groupClassName?: string;
    onValueUpdate?: (name: string) => void;
    className?: string;
}

interface IState {
    triggerOnValueUpdate?: boolean;
    error?: string | null;
}

interface ValidationResult {
    success: boolean;
    error: string | null;
}

export class FormFile extends React.Component<IProps, IState> {
    private defaultInputValue = {
        name: "",
        base64: null,
        url: null
    };
    private inputRef: any;

    constructor(props: IProps) {
        super(props);

        this.onChange = this.onChange.bind(this);
        this.clearField = this.clearField.bind(this);
        this.state = {
            triggerOnValueUpdate: false,
            error: null
        };
    }

    public componentDidUpdate() {
        const {onValueUpdate} = this.props;
        if (this.state.triggerOnValueUpdate) {
            if (isFunction(onValueUpdate)) {
                this.setState({triggerOnValueUpdate: false});
                onValueUpdate(this.props.name);
            }
        }
    }

    private onValidationSuccess(file: File) {
        const FR = new FileReader();
        FR.onload = (e: any) => {
            const base64 = e.target.result;
            this.props.onChange(this.props.name, {
                name: file.name,
                base64,
                url: null
            });
            this.setState({error: null, triggerOnValueUpdate: true});
        };
        FR.readAsDataURL(file);
    }

    private isFileValid(file: any): ValidationResult {
        const {props} = this;
        const resultSuccess = {
            success: true,
            error: null
        };
        const resultFailed = {
            success: false,
            error: ""
        };

        let fileSplit = file.name
            .toLowerCase()
            .split(".");
        let ext = fileSplit[fileSplit.length - 1];
        ext = ext || "";

        let errorMessage = null;
        let sizeIsValid = true;
        let extensionIsValid = true;

        if (props.allowedExtensions) {
            extensionIsValid = !(
                props.allowedExtensions
                && props.allowedExtensions.length
                && props.allowedExtensions.indexOf(ext) === -1
            );
            if (!extensionIsValid) {
                const extensions = props.allowedExtensions.join(", ");
                resultFailed.error = "Niepoprawny format pliku. Wspierane formaty to: " + extensions;
                return resultFailed;
            }
        }
        if (props.maxSize) {
            const maxSize = parseInt(props.maxSize, 10);
            sizeIsValid = file.size <= maxSize;
            if (!sizeIsValid) {
                const fileSize = (maxSize / 1024 ** 2).toFixed(2);
                resultFailed.error = "Plik jest zbyt duży. Rozmiar pliku nie może przekroczyć: " + fileSize + " MB";
                return resultFailed;
            }
        }
        return resultSuccess;
    }

    private onChange(e: React.SyntheticEvent) {
        const target = e.target as any;
        let file = target.files && target.files.length && target.files[0];
        if (file) {
            const validationResult = this.isFileValid(file);
            if (validationResult.success) {
                this.onValidationSuccess(file);
            } else {
                this.setState({error: validationResult.error});
                this.props.onChange(this.props.name, this.defaultInputValue);
            }
        }
    }

    private clearField() {
        this.props.onChange(this.props.name, this.defaultInputValue);
        this.inputRef.value = "";
        this.setState({triggerOnValueUpdate: true});
    }

    private renderChooseButton(): JSX.Element {
        const {value, error} = this.props;
        return (
            <label className={`btn btn-default choose-button${value && value.name ? "-short" : ""}${error ? "-error" : ""}`} htmlFor={this.props.name}>
                {(value && value.name) ? "Zmień" : "Wybierz"}
            </label>
        );
    }

    private renderCancelButton(): JSX.Element {
        const {value, error} = this.props;
        return (
            <Tooltip content="Odrzuć plik" id={this.props.name} placement="top">
                <div className={`btn btn-default file-delete${value && value.name ? "-visible" : ""}${error ? "-error" : ""}`} onClick={this.clearField}>
                    <Icon icon="delete" />
                </div>
            </Tooltip>
        );
    }

    public render(): JSX.Element {
        const {error, label, className, allowedExtensions, groupClassName} = this.props;
        const holderClassName = classnames("form-group", groupClassName, error && "has-error");
        const fieldClassName = classnames("form-control", className);

        const errorMessage = error ? error.join("\n") : this.state.error;

        const allowed = allowedExtensions
            && allowedExtensions.map(
                (ext: string): string => "." + ext
            ).join(",");

        const field = (
            <input
                ref={input => this.inputRef = input}
                name={this.props.name}
                id={this.props.name}
                className={`${fieldClassName} file-field`}
                type="file"
                accept={allowed}
                onChange={this.onChange}
            />
        );

        const renderField = (
            <div>
                {field}
                <div className="render-field-holder">
                    <div className="field-holder">
                        <label className="field-label" htmlFor={this.props.name} />
                        <FormInput
                            type="text"
                            name={`${this.props.name}__placeholder`}
                            placeholder={this.props.value && this.props.value.name || "Brak pliku"}
                            disabled
                            className="field-input"
                            groupClassName="mb-0"
                            onChange={() => null}
                            onAfterChange={() => null}
                            value={""}
                        />
                    </div>
                    <div className="button-holder">
                        {this.renderChooseButton()}
                        {this.renderCancelButton()}
                    </div>
                </div>
            </div>
        );

        return (
            <div className={holderClassName}>
                {label && <FieldLabel label={label} htmlFor={this.props.name}/>}
                {errorMessage ? <FieldError error={errorMessage}>{renderField}</FieldError> : renderField}
            </div>
        );
    }
}
